import type { Parcel } from "./types";

/**
 * Mirrors apps/customer/src/domain/parcelTypes.ts and parcelCategories.ts --
 * same reasoning as types.ts's own header comment: the mobile app and this
 * one are separate bundles, so this is copied rather than imported, but the
 * catalogue itself must not drift between the two.
 */
export interface ParcelType {
  id: string;
  label: string;
  hint: string;
  preset: Omit<Parcel, "description">;
}

export const PARCEL_TYPES: ParcelType[] = [
  {
    id: "documents",
    label: "Documents",
    hint: "Loose printed documents, A4 or smaller",
    preset: { length_mm: 350, width_mm: 250, height_mm: 10, weight_g: 250 },
  },
  {
    id: "flyer",
    label: "Flyer",
    hint: "Soft-sided, small and light, up to 2 kg",
    preset: { length_mm: 400, width_mm: 300, height_mm: 80, weight_g: 1000 },
  },
  {
    id: "small_parcel",
    label: "Small parcel",
    hint: "Shoebox sized, up to 5 kg",
    preset: { length_mm: 300, width_mm: 230, height_mm: 300, weight_g: 2000 },
  },
  {
    id: "medium_parcel",
    label: "Medium parcel",
    hint: "Moving-box sized, up to 10 kg",
    preset: { length_mm: 600, width_mm: 410, height_mm: 190, weight_g: 5000 },
  },
  {
    id: "large_parcel",
    label: "Large parcel",
    hint: "Bulky, up to 50 kg",
    preset: { length_mm: 1000, width_mm: 1000, height_mm: 1000, weight_g: 15000 },
  },
  {
    id: "international_package",
    label: "International package",
    hint: "Crossing a border -- sizes vary, adjust the dimensions below",
    preset: { length_mm: 450, width_mm: 350, height_mm: 300, weight_g: 5000 },
  },
  {
    id: "excess_baggage",
    label: "Excess baggage",
    hint: "A bag or case -- sizes vary, adjust the dimensions below",
    preset: { length_mm: 700, width_mm: 450, height_mm: 300, weight_g: 20000 },
  },
  {
    id: "other",
    label: "Other",
    hint: "Enter your own dimensions",
    preset: { length_mm: 300, width_mm: 200, height_mm: 150, weight_g: 1000 },
  },
];

export const EMPTY_PARCEL: Parcel = { description: "", length_mm: 300, width_mm: 200, height_mm: 150, weight_g: 1000 };

/**
 * "What's inside?" -- separate from the parcel type, which is about size and
 * form factor.
 *
 * Allowed first, refused last, and the array order is the order on screen. The
 * list used to be built the other way round, from standard courier exclusion
 * lists: almost every line carried a "-- restricted" or "-- prohibited" suffix,
 * so a customer opening the picker was told, in effect, that we carry nothing.
 * A list of what we do carry is the useful half, and it is what people are
 * looking for when they open it.
 *
 * Levels:
 *   none        we carry it
 *   prohibited  we will not carry it, and a booking is blocked
 *
 * `note` is guidance once an item is chosen -- how to pack it, what cover it
 * has. It never means the parcel cannot ship.
 */
export type RestrictionLevel = "none" | "prohibited";

export interface ParcelCategory {
  id: string;
  label: string;
  level: RestrictionLevel;
  note?: string;
}

export const PARCEL_CATEGORIES: ParcelCategory[] = [
  // ---------- What we carry ----------
  { id: "documents", label: "Documents & paperwork", level: "none" },
  { id: "clothing_household", label: "Clothing, shoes & household goods", level: "none" },
  { id: "general_merchandise", label: "General merchandise & online orders", level: "none" },
  {
    id: "beauty",
    label: "Perfume, skincare & cosmetics",
    level: "none",
    note: "Seal anything that can leak, bag it, and pack it upright.",
  },
  { id: "hair", label: "Hair, wigs & extensions", level: "none" },
  {
    id: "electronics",
    label: "Electronics & small appliances",
    level: "none",
    note: "Batteries must stay fitted in the device. Loose batteries and power banks we cannot carry.",
  },
  {
    id: "food",
    label: "Food: baked goods, dry and non-perishable",
    level: "none",
    note: "Nothing that needs refrigeration or will spoil in transit.",
  },
  {
    id: "fragile",
    label: "Fragile items: glass, crockery, picture frames, mirrors",
    level: "none",
    note: "Pack it to survive a fall from waist height. Damage to under-packed fragile goods is not covered.",
  },
  {
    id: "artwork",
    label: "Artwork, paintings & sculptures",
    level: "none",
    note: "Declare what it is really worth and add cover, otherwise a claim pays out at the standard limit.",
  },
  {
    id: "jewellery",
    label: "Jewellery, watches & accessories",
    level: "none",
    note: "Declare what it is really worth and add cover, otherwise a claim pays out at the standard limit.",
  },
  {
    id: "gifting",
    label: "Gift boxes, hampers & party goods",
    level: "none",
    note: "A hamper must not contain alcohol.",
  },
  {
    id: "furniture_small",
    label: "Small furniture & flat-pack items",
    level: "none",
    note: "It has to fit a van and be liftable by one person. Ask us first for anything larger.",
  },
  {
    id: "liquids",
    label: "Liquids, sealed and non-hazardous",
    level: "none",
    note: "Sealed containers only, bagged and packed upright.",
  },
  {
    id: "other",
    label: "Something else",
    level: "none",
    note: "Say what it is in the description below so we can check it travels safely.",
  },

  // ---------- What we will not carry ----------
  { id: "cash_valuables", label: "Cash, bank cards, cheques, bullion & securities", level: "prohibited" },
  { id: "alcohol", label: "Alcohol", level: "prohibited" },
  { id: "animals", label: "Live animals & livestock", level: "prohibited" },
  { id: "medical", label: "Medical specimens & biological samples", level: "prohibited" },
  { id: "human_remains", label: "Human remains", level: "prohibited" },
  { id: "weapons", label: "Weapons & firearms", level: "prohibited" },
  { id: "ammunition", label: "Ammunition", level: "prohibited" },
  { id: "explosives", label: "Explosives & fireworks", level: "prohibited" },
  { id: "illegal", label: "Illegal substances or items", level: "prohibited" },
  { id: "flammable_liquids", label: "Flammable liquids, paints, thinners & fuels", level: "prohibited" },
  { id: "gas", label: "Compressed or flammable gas: aerosols, butane, fire extinguishers", level: "prohibited" },
  { id: "toxic", label: "Toxic, poisonous or infectious substances", level: "prohibited" },
  { id: "acids", label: "Acids & corrosive chemicals", level: "prohibited" },
  { id: "combustibles", label: "Matches, charcoal & fire lighters", level: "prohibited" },
  { id: "agricultural", label: "Fertiliser & pesticides", level: "prohibited" },
  { id: "batteries", label: "Loose batteries & power banks", level: "prohibited" },
  { id: "vehicle_parts", label: "Vehicle parts holding fuel, oil or other liquid", level: "prohibited" },
  { id: "hazardous_other", label: "Anything else classed as dangerous goods", level: "prohibited" },
];

/** The two groups, in the order they appear. */
export const CARRIED = PARCEL_CATEGORIES.filter((c) => c.level === "none");
export const NOT_CARRIED = PARCEL_CATEGORIES.filter((c) => c.level === "prohibited");

export function categoryById(id: string | null): ParcelCategory | undefined {
  return PARCEL_CATEGORIES.find((category) => category.id === id);
}

/**
 * What to say about a chosen category: either why it cannot ship, or how to
 * pack it. Returning the tone as well lets the caller colour it, so a packing
 * tip does not read like a refusal.
 */
export function categoryMessage(
  category: ParcelCategory | undefined,
): { tone: "bad" | "info"; text: string } | null {
  if (!category) return null;
  if (category.level === "prohibited") {
    return {
      tone: "bad",
      text: "We cannot carry this. Choose another item, or contact us if you think this is wrong.",
    };
  }
  return category.note ? { tone: "info", text: category.note } : null;
}
